
import React, { useState, useEffect, useRef } from 'react';
import { Button } from "@/components/ui/button";
import { Building, Star, ExternalLink, Edit } from 'lucide-react';
import EditCompanyModal from './EditCompanyModal';
import CompanyHeader from './CompanyHeader';
import PageScreenshotViewer from '../pages/PageScreenshotViewer';
import PageEventViewer from '../pages/PageEventViewer';
import PageAskAI from '../pages/PageAskAI';
import CompanyPricingMetrics from '../companies/CompanyPricingMetrics';
import CompanyTags from '../companies/CompanyTags';
import { VersionProvider } from '@/contexts/VersionContext';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ScrollArea, ScrollBar } from "@/components/ui/scroll-area";
import type { Canvas as FabricCanvas } from "fabric/fabric-impl";

// Helper function to format page labels
const formatPageLabel = (subslug: string | undefined): string => {
  if (!subslug || subslug === 'main') return 'Main';
  return subslug.charAt(0).toUpperCase() + subslug.slice(1).replace(/-/g, ' ');
};

interface CompanyDetailsProps {
  company: any;
  onToggleProject: () => Promise<void>;
  isInProject: boolean;
  onSaveCanvas: (canvas: FabricCanvas) => Promise<void>;
}

const CompanyDetails = ({
  company,
  onToggleProject,
  isInProject,
  onSaveCanvas,
}: CompanyDetailsProps) => {
  const [showEditModal, setShowEditModal] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [showRemoveDialog, setShowRemoveDialog] = useState(false);
  const [currentCompany, setCurrentCompany] = useState(company);
  const contentRef = useRef<HTMLDivElement>(null);

  const pages = company.pages || [];
  const defaultTab = pages[0]?.subslug || "main";

  // Get screenshot URL for the current company
  const screenshotUrl = company.screenshotUrl || pages[0]?.screenshotUrl || null;
  // Get pricing page URL for the current company
  // Note: We'll use the pages directly in the CompanyHeader component

  // Update current company and scroll to top whenever company changes
  useEffect(() => {
    setCurrentCompany(company);
    if (contentRef.current) {
      contentRef.current.scrollTop = 0;
    }
  }, [company]);

  console.log('✅Safe to remove!!');

  return (
    <div className="relative w-full h-full flex flex-col min-w-0">
      <div className="px-4 py-3">
        <div className="flex items-start gap-4 min-w-0">
          <div className="flex flex-col items-start gap-1">
            <div className="w-12 h-12 relative">
              {currentCompany.logoUrl && (
                <>
                  <img
                    src={currentCompany.logoUrl}
                    alt={`${currentCompany.slug} logo`}
                    className="w-full h-full object-contain"
                  />
                  <div className="absolute -bottom-1 -right-1 bg-white rounded-full p-0.5 shadow-sm border-2 border-white">
                    <Building className="h-4 w-4 text-gray-500" />
                  </div>
                </>
              )}
            </div>
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <h3 className="font-semibold text-lg text-gray-900 capitalize truncate">
                  {currentCompany.slug.replace(/-/g, ' ')}
                </h3>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => isInProject ? setShowRemoveDialog(true) : onToggleProject()}
                  className={`p-2 rounded-full transition-colors flex-shrink-0 ${isInProject
                    ? 'bg-yellow-100 hover:bg-yellow-200 text-yellow-600'
                    : 'bg-gray-100 hover:bg-gray-200 text-gray-400'
                    }`}
                  title={isInProject ? `Remove ${currentCompany.slug} from project` : `Add ${currentCompany.slug} to project`}
                >
                  <Star
                    className={`h-5 w-5 ${isInProject ? 'fill-current' : ''}`}
                  />
                </button>
              </div>
            </div>
            <div className="space-y-6">
              {currentCompany.description && (
                <p className="mt-1 text-sm text-gray-600 mr-20">{currentCompany.description}</p>
              )}
              <CompanyHeader
                company={currentCompany}
                onEdit={() => setShowEditModal(true)}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Edit Company Modal */}
      <EditCompanyModal
        company={currentCompany}
        isOpen={showEditModal}
        onClose={() => setShowEditModal(false)}
        onCompanyUpdated={(updatedCompany) => {
          // Update the local state with the updated company
          setCurrentCompany(updatedCompany);
          console.log('✅ [CompanyDetails] Company updated:', updatedCompany);
        }}
      />

      <div ref={contentRef} className="flex-1 w-full overflow-y-auto">
        {pages.length === 1 ? (
          <div className="px-4 space-y-6">
            <CompanyTags company={currentCompany} />
            <VersionProvider>
              <PageScreenshotViewer slug={currentCompany.slug} />
            </VersionProvider>
            {/* Always show pricing metrics component, it will handle empty state internally */}
            <CompanyPricingMetrics
              company={currentCompany}
              refreshCompanyData={async () => {
                // Reload the current company data
                if (onSaveCanvas) {
                  await onSaveCanvas();
                }
              }}
            />
            <PageEventViewer slug={currentCompany.slug} />
            <PageAskAI slug={currentCompany.slug} />
          </div>
        ) : (
          <Tabs defaultValue={defaultTab} className="w-full">
            <div className="px-4 mt-9">
              <ScrollArea className="w-full whitespace-nowrap">
                <TabsList className="w-full h-12 justify-start bg-transparent border-b rounded-none">
                  {pages.map((page: any, index: number) => (
                    <TabsTrigger
                      key={index}
                      value={page.subslug || "main"}
                      className="flex-1 px-4 py-2 data-[state=active]:border-b-2 data-[state=active]:border-primary data-[state=active]:text-primary rounded-none transition-all duration-200"
                    >
                      {formatPageLabel(page.subslug)}
                    </TabsTrigger>
                  ))}
                </TabsList>
                <ScrollBar orientation="horizontal" className="h-2.5" />
              </ScrollArea>
            </div>
            <div className="px-4">
              {pages.map((page: any, index: number) => (
                <TabsContent key={index} value={page.subslug || "main"} className="space-y-6">
                  <CompanyTags
                    company={currentCompany}
                    pageSubslug={page.subslug || 'main'}
                  />
                  <VersionProvider>
                    <PageScreenshotViewer
                      key={`screenshot-${currentCompany.slug}-${page.subslug || 'main'}`}
                      slug={currentCompany.slug}
                      subslug={page.subslug || ''}
                    />
                  </VersionProvider>
                  {/* Debug info */}
                  {process.env.NODE_ENV === 'development' && (
                    <div className="text-xs text-gray-400 mt-1 mb-4">
                      Debug: Showing screenshot for {currentCompany.slug}{page.subslug || ''}
                    </div>
                  )}
                  {/* Always show pricing metrics component, it will handle empty state internally */}
                  <CompanyPricingMetrics
                    company={currentCompany}
                    pageSubslug={page.subslug || 'main'}
                    refreshCompanyData={async () => {
                      // Reload the current company data
                      if (onSaveCanvas) {
                        await onSaveCanvas();
                      }
                    }}
                  />
                  <PageEventViewer slug={currentCompany.slug} />
                  <PageAskAI slug={currentCompany.slug} />
                </TabsContent>
              ))}
            </div>
          </Tabs>
        )}
      </div>

      <AlertDialog open={showRemoveDialog} onOpenChange={setShowRemoveDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove from Swipe File?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to remove this company from your swipe file? This will also remove any annotations you've made.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => {
              onToggleProject();
              setShowRemoveDialog(false);
            }}>
              Remove
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default CompanyDetails;
